// Prompt builders for the report feed's language-model enrichment (S-2 assessments, SITREP phrasing) and
// for the free-text FRAGO (fragmentary order) card. Kept apart from any `src/ai/prompts.ts` the advisors
// work adds, to avoid the two efforts colliding on one file - see plan Task 18.
//
// Every builder here follows spec §6's "grounded narration" pattern: the system block states the
// grounding rule (assess only the listed facts, cite event ids, label uncertainty), and the user block
// lists exactly the facts the model is allowed to use. `src/ai/validate.ts` re-checks citations and
// legality after the call returns - nothing here is trusted on the model's word alone.

import { CARDS } from '../sim/deck';
import { UNIT_DEFS } from '../sim/scenario';
import type { Contact, Event, Graphic, Unit } from '../sim/types';
import type { Grid } from '../sim/grid';

export interface LlmPrompt { system: string; user: string }

/** Four-digit grid reference for a terrain cell, matching `src/ui/reports.ts` `gridRef` (not imported
 * from there to keep the AI layer free of a dependency on the UI layer). */
function gridRefOf(cell: number | undefined, grid: Grid | null): string {
  if (cell === undefined || !grid) return '----';
  const gx = Math.min(99, Math.floor((grid.cx(cell) * 100) / grid.w));
  const gy = Math.min(99, Math.floor((grid.cy(cell) * 100) / grid.h));
  return `${String(gx).padStart(2, '0')}${String(gy).padStart(2, '0')}`;
}

function cellNear(grid: Grid | null, p: { x: number; y: number } | undefined): number | undefined {
  if (!grid || !p) return undefined;
  return grid.cellOfCm(p.x, p.y);
}

// ---- S-2 assessment ---------------------------------------------------------------------------------

export interface S2Context {
  /** Up to the last 10 events, most recent last. */
  recentEvents: Event[];
  /** Blue's current picture of the enemy. */
  contacts: Contact[];
  /** Named areas of interest only (filtered from `scenario.graphics`). */
  nais: Graphic[];
  /** `scenario.names.enemy`. */
  enemyDesc: string;
  grid: Grid | null;
}

const S2_SYSTEM = [
  'You are the S-2 (intelligence officer) on a battalion staff, writing a one- or two-sentence assessment',
  'for the unit report feed of a live wargame. Assess only from the facts listed in this prompt - never',
  'invent a contact, a position, a unit type, an event or a grid reference that is not listed below.',
  'List the ids of every event your assessment relies on in `cited_event_ids`. Label your own uncertainty',
  'honestly in `confidence`: "low" when the picture is thin, contradictory or a single unconfirmed report;',
  '"medium" for one clear but unconfirmed report; "high" only when multiple corroborating reports agree.',
  'Write the way a staff officer talks on the net - terse, no hedging language beyond the confidence field',
  'itself, no restating the whole event log back verbatim.',
].join(' ');

function eventLine(e: Event, grid: Grid | null): string {
  const cell = e.cell !== undefined ? ` cell=${e.cell} vic=${gridRefOf(e.cell, grid)}` : '';
  return `#${e.id} t=${e.t} ${e.kind}${cell} ${JSON.stringify(e.payload)}`;
}

function contactLine(c: Contact, grid: Grid | null): string {
  const cell = cellNear(grid, c);
  return `enemy #${c.enemyId} type=${c.typeConf} lastSeen=t${c.lastSeen} vic=${gridRefOf(cell, grid)} errorM=${Math.round(c.errorM)}`;
}

function naiLine(g: Graphic, grid: Grid | null): string {
  const cell = cellNear(grid, g.pts[0]);
  return `${g.name} id=${g.id} vic=${gridRefOf(cell, grid)}`;
}

/** The S-2's grounding prompt for one significant contact (`contact.new`/`nai.contact`) event. */
export function buildS2Prompt(ev: Event, ctx: S2Context): LlmPrompt {
  const user = [
    `Triggering event: ${ev.side === 0 ? eventLine(ev, ctx.grid) : '(no friendly report)'}`,
    '',
    'Recent events (most recent last):',
    ctx.recentEvents.filter((e) => e.side === 0).map((e) => eventLine(e, ctx.grid)).join('\n') || '(none)',
    '',
    'Current contacts:',
    ctx.contacts.map((c) => contactLine(c, ctx.grid)).join('\n') || '(none)',
    '',
    'Named areas of interest:',
    ctx.nais.map((g) => naiLine(g, ctx.grid)).join('\n') || '(none)',
    '',
    `Enemy: ${ctx.enemyDesc}`,
  ].join('\n');

  return { system: S2_SYSTEM, user };
}

// ---- SITREP phrasing ---------------------------------------------------------------------------------

export interface SitrepContext {
  /** Up to the last 10 events, most recent last, for tone/context only - never a new source of facts. */
  recentEvents: Event[];
  phase: string;
}

const SITREP_SYSTEM = [
  'You are the battle captain on a battalion staff, rephrasing an already-computed situation report',
  '(SITREP) for the unit report feed of a live wargame. The numbers and facts in the given SITREP text',
  'are already correct and already verified - your only job is wording, not analysis. Rephrase it into',
  'exactly two short sentences of natural staff prose. Do not add, change or drop any fact, number or',
  'claim that is not already in the given text.',
  'Return the rephrased text in `text`. Set `confidence` to "high", since this is a rephrasing of already-',
  'verified data, not a new assessment. Cite the ids of any recent events your phrasing explicitly names;',
  'it is fine for `cited_event_ids` to be empty.',
].join(' ');

/** Haiku's rephrasing prompt for the feed's own periodic SITREP text (`sitrepReport` in `src/ui/reports.ts`). */
export function buildSitrepPrompt(template: string, ctx: SitrepContext): LlmPrompt {
  const user = [
    `Phase: ${ctx.phase}`,
    `Computed SITREP: ${template}`,
    '',
    'Recent events for context (most recent last, do not cite facts from these that are not in the SITREP above):',
    ctx.recentEvents.filter((e) => e.side === 0).map((e) => `#${e.id} t=${e.t} ${e.kind}`).join('\n') || '(none)',
  ].join('\n');

  return { system: SITREP_SYSTEM, user };
}

// ---- FRAGO parsing -------------------------------------------------------------------------------------

export interface FragoContext {
  /** Friendly (blue), living units only. */
  units: Unit[];
  /** Every control graphic (phase lines, the objective, named areas of interest, axes, assembly areas). */
  graphics: Graphic[];
  grid: Grid | null;
}

const FRAGO_SYSTEM = [
  'You are the S-3 (operations) assistant parsing a free-text fragmentary order (FRAGO) into structured',
  'order cards for a live wargame. Only use the units and control graphics listed in this prompt - never',
  'invent a unit id, callsign or graphic that is not listed.',
  'Unit references may be exact callsigns (e.g. "A11", "B21") or plain descriptions (e.g. "2nd platoon of',
  'A company", "the recon platoon", "the artillery") - resolve a description to the single closest-',
  'matching unit in the list; if more than one unit could match, prefer the strongest fit and leave the',
  'rest to `unparsed` rather than guessing at several.',
  'Each order needs `unitId` (the numeric id from the unit list, never the callsign), `card` (one of that',
  'unit\'s own listed legal card ids), and a `target` whose `kind` is exactly the kind printed after that',
  'card id in the unit\'s list - `card:point` takes {"kind":"point","cell":N}, `card:graphic` takes',
  '{"kind":"graphic","graphicId":"..."}, `card:unit` takes {"kind":"unit","unitId":N}, `card:none` takes',
  '{"kind":"none"}. Naming a control graphic in the order text does not make the target kind "graphic":',
  'a graphic is also a place, so for a `:point` card (attack, move, defend, withdraw, fire missions,',
  'smoke, suppress, launch UAS) use the `cell` printed next to that graphic, and only ever send',
  '`graphicId` for a `:graphic` card (screen, recon, observe). An order whose kind does not match its',
  'card is dropped before the player sees it.',
  'Return every order you can confidently map in `orders[]`. Put any remaining text you could not',
  'confidently map to a legal order, verbatim, in `unparsed` - never guess at a unit, card or graphic that',
  'is not listed above.',
].join(' ');

/**
 * One unit as the FRAGO parser sees it. Each legal card carries its target kind (`attack:point`),
 * because the card id alone does not say what a target may be: the model used to read "A11 attack PL
 * RED" as a graphic target for a point card, and `validatePlay` then dropped the only order in the
 * FRAGO. The advisor prompt has always stated the kinds (`deckFacts` in src/ai/prompts.ts) and its
 * orders validate; this brings the FRAGO prompt in line.
 */
function unitLine(u: Unit, grid: Grid | null): string {
  const legal = CARDS.filter((c) => c.allowedTypes.includes(u.type)).map((c) => `${c.id}:${c.targetKind}`).join(',');
  const cell = grid ? grid.cellOfCm(u.x, u.y) : undefined;
  return `#${u.id} ${u.callsign} (${UNIT_DEFS[u.type].label}) cell=${cell ?? '?'} legal cards: ${legal}`;
}

function graphicLine(g: Graphic, grid: Grid | null): string {
  const cell = cellNear(grid, g.pts[0]);
  return `${g.name} [${g.kind}] id=${g.id} cell=${cell ?? '?'}`;
}

/** The FRAGO card's parse prompt: free text -> the same `orders[]`/`unparsed` shape the deterministic
 * fallback parser (`src/ai/fragoParse.ts`) returns, so the confirmation UI does not care which produced it. */
export function buildFragoPrompt(text: string, ctx: FragoContext): LlmPrompt {
  const user = [
    `Order text: "${text.trim()}"`,
    '',
    'Friendly units:',
    ctx.units.map((u) => unitLine(u, ctx.grid)).join('\n') || '(none)',
    '',
    'Control graphics:',
    ctx.graphics.map((g) => graphicLine(g, ctx.grid)).join('\n') || '(none)',
  ].join('\n');

  return { system: FRAGO_SYSTEM, user };
}
